import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '../components/ui/alert-dialog';
import { Clock, ChevronLeft, ChevronRight, Flag, Send, Trash2, Bookmark } from 'lucide-react';
import { toast } from 'sonner';

export default function CBTEngine() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeSpent, setTimeSpent] = useState({});
  const [markedForReview, setMarkedForReview] = useState({});
  const [visited, setVisited] = useState({});
  const [remainingTime, setRemainingTime] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const questionTimerRef = useRef(null);
  const autoSaveRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    loadAttempt();
    return () => {
      clearInterval(timerRef.current);
      clearInterval(autoSaveRef.current);
      clearInterval(questionTimerRef.current);
    };
  }, [attemptId]);

  async function loadAttempt() {
    try {
      const cached = localStorage.getItem(`attempt_${attemptId}`);
      let data;
      const localData = cached ? JSON.parse(cached) : null;

      try {
        const res = await api.get(`/student/attempts/${attemptId}`);
        data = res.data;
        if (data.is_completed) {
          navigate(`/results/${attemptId}`, { replace: true });
          return;
        }
      } catch {
        if (localData?.questions) {
          data = localData;
        } else {
          toast.error('Failed to load test');
          navigate('/dashboard');
          return;
        }
      }

      setAttempt(data);
      setQuestions(data.questions || []);

      // Restore saved state
      if (localData?.answers) setAnswers(localData.answers);
      else if (data.answers && Object.keys(data.answers).length > 0) setAnswers(data.answers);

      if (localData?.timeSpent) setTimeSpent(localData.timeSpent);
      else if (data.time_spent && Object.keys(data.time_spent).length > 0) setTimeSpent(data.time_spent);

      if (localData?.markedForReview) setMarkedForReview(localData.markedForReview);
      if (localData?.visited) setVisited(localData.visited);

      // Calculate remaining time from server
      const startTime = new Date(data.start_time).getTime();
      const durationMs = (data.duration_minutes || 60) * 60 * 1000;
      const endTime = startTime + durationMs;
      const remaining = Math.max(0, Math.floor((endTime - Date.now()) / 1000));
      setRemainingTime(remaining);

      if (remaining <= 0) {
        handleAutoSubmit(data);
        return;
      }

      // Mark first question visited
      if (data.questions?.length > 0) {
        setVisited(prev => ({ ...prev, [data.questions[0].id]: true }));
      }

      setLoading(false);
    } catch (err) {
      toast.error('Failed to load test');
      navigate('/dashboard');
    }
  }

  // Timer
  useEffect(() => {
    if (loading || remainingTime <= 0) return;
    timerRef.current = setInterval(() => {
      setRemainingTime(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [loading]);

  // Question timer
  useEffect(() => {
    if (loading || !questions[currentIndex]) return;
    const qid = questions[currentIndex].id;
    questionTimerRef.current = setInterval(() => {
      setTimeSpent(prev => ({ ...prev, [qid]: (prev[qid] || 0) + 1 }));
    }, 1000);
    return () => clearInterval(questionTimerRef.current);
  }, [currentIndex, loading, questions]);

  // Auto-save every 30 seconds
  useEffect(() => {
    if (loading) return;
    autoSaveRef.current = setInterval(() => saveProgress(), 30000);
    return () => clearInterval(autoSaveRef.current);
  }, [loading, answers, timeSpent]);

  // Save to localStorage on change
  useEffect(() => {
    if (!attemptId || loading) return;
    localStorage.setItem(`attempt_${attemptId}`, JSON.stringify({
      answers, timeSpent, markedForReview, visited, currentIndex
    }));
  }, [answers, timeSpent, markedForReview, visited, currentIndex, attemptId, loading]);

  const saveProgress = useCallback(async () => {
    try {
      await api.put(`/student/attempts/${attemptId}/save`, { answers, time_spent: timeSpent });
    } catch {}
  }, [attemptId, answers, timeSpent]);

  async function handleAutoSubmit(data) {
    setSubmitting(true);
    try {
      const res = await api.post(`/student/attempts/${attemptId}/submit`, { answers, time_spent: timeSpent });
      localStorage.removeItem(`attempt_${attemptId}`);
      toast.info('Time is up! Test auto-submitted.');
      navigate(`/results/${attemptId}`, { replace: true });
    } catch {
      toast.error('Failed to submit');
      setSubmitting(false);
    }
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      await saveProgress();
      await api.post(`/student/attempts/${attemptId}/submit`, { answers, time_spent: timeSpent });
      localStorage.removeItem(`attempt_${attemptId}`);
      toast.success('Test submitted successfully!');
      navigate(`/results/${attemptId}`, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
      setSubmitting(false);
    }
  }

  function selectOption(qid, option) {
    setAnswers(prev => ({ ...prev, [qid]: { selected: option } }));
  }

  function clearResponse() {
    const qid = questions[currentIndex]?.id;
    if (qid) setAnswers(prev => { const n = { ...prev }; delete n[qid]; return n; });
  }

  function toggleMark() {
    const qid = questions[currentIndex]?.id;
    if (qid) setMarkedForReview(prev => ({ ...prev, [qid]: !prev[qid] }));
  }

  function goToQuestion(idx) {
    setCurrentIndex(idx);
    const qid = questions[idx]?.id;
    if (qid) setVisited(prev => ({ ...prev, [qid]: true }));
    setShowPalette(false);
  }

  function getQuestionStatus(qid) {
    if (answers[qid]?.selected) return 'answered';
    if (markedForReview[qid]) return 'marked';
    if (visited[qid]) return 'not-answered';
    return 'not-visited';
  }

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="w-8 h-8 border-4 border-[#7551e8] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const currentQ = questions[currentIndex];
  const answeredCount = Object.keys(answers).filter(k => answers[k]?.selected).length;
  const notAnsweredCount = Object.keys(visited).length - answeredCount;
  const markedCount = Object.keys(markedForReview).filter(k => markedForReview[k]).length;

  return (
    <div className="cbt-container flex flex-col bg-white" data-testid="cbt-engine">
      {/* Top Bar */}
      <div className="h-14 border-b border-zinc-200 flex items-center justify-between px-4 bg-white shrink-0">
        <h1 className="font-['Outfit'] font-medium text-[#0A0A0A] truncate text-sm sm:text-base" data-testid="cbt-test-title">
          {attempt?.test_title || 'Mock Test'}
        </h1>
        <div className="flex items-center gap-3">
          <Badge className={`font-mono text-sm px-3 py-1 ${remainingTime < 300 ? 'bg-red-50 text-red-600 border-red-200' : 'bg-[#F4F0FF] text-[#7551e8] border-[#7551e8]/20'}`} data-testid="cbt-timer">
            <Clock size={14} className="mr-1.5" /> {formatTime(remainingTime)}
          </Badge>
          <Button variant="outline" size="sm" className="lg:hidden" onClick={() => setShowPalette(!showPalette)} data-testid="toggle-palette-btn">
            <Flag size={14} />
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-y-auto">
          {/* Subject Tabs */}
          {attempt?.subjects?.length > 0 && (
            <div className="border-b border-zinc-200 px-4 flex gap-1 overflow-x-auto bg-zinc-50 shrink-0">
              {attempt.subjects.map(sub => (
                <button key={sub} className="px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 border-transparent hover:border-[#7551e8] hover:text-[#7551e8] transition-colors"
                  onClick={() => {
                    const idx = questions.findIndex(q => q.subject === sub);
                    if (idx >= 0) goToQuestion(idx);
                  }}>{sub}</button>
              ))}
            </div>
          )}

          {/* Question */}
          {currentQ && (
            <div className="flex-1 p-4 sm:p-6 overflow-y-auto" data-testid="question-area">
              <div className="max-w-3xl mx-auto">
                <div className="flex items-center gap-2 mb-4">
                  <Badge variant="outline" className="text-xs">Q.{currentIndex + 1}/{questions.length}</Badge>
                  {currentQ.marks && <Badge variant="outline" className="text-xs">{currentQ.marks} marks</Badge>}
                  {currentQ.subject && <Badge className="bg-[#F4F0FF] text-[#7551e8] border-0 text-xs">{currentQ.subject}</Badge>}
                </div>
                <div className="text-[#0A0A0A] text-base leading-relaxed mb-6 whitespace-pre-wrap" data-testid="question-text">
                  {currentQ.question_text}
                </div>
                {currentQ.image_url && <img src={currentQ.image_url} alt="Question" className="max-w-full rounded-lg mb-6 border" />}

                <div className="space-y-3">
                  {['a', 'b', 'c', 'd'].map(opt => {
                    const isSelected = answers[currentQ.id]?.selected === opt;
                    return (
                      <button key={opt} onClick={() => selectOption(currentQ.id, opt)}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-all ${isSelected ? 'border-[#7551e8] bg-[#F4F0FF]' : 'border-zinc-200 hover:border-zinc-300 bg-white'}`}
                        data-testid={`option-${opt}`}>
                        <div className="flex items-start gap-3">
                          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${isSelected ? 'bg-[#7551e8] text-white' : 'bg-zinc-100 text-[#52525B]'}`}>
                            {opt.toUpperCase()}
                          </span>
                          <span className="text-[#0A0A0A] whitespace-pre-wrap">{currentQ[`option_${opt}`]}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="border-t border-zinc-200 px-4 py-3 flex items-center justify-between gap-2 bg-white shrink-0">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={clearResponse} data-testid="clear-response-btn">
                <Trash2 size={14} className="mr-1" /> Clear
              </Button>
              <Button variant="outline" size="sm" onClick={toggleMark}
                className={markedForReview[currentQ?.id] ? 'border-[#7551e8] text-[#7551e8]' : ''}
                data-testid="mark-review-btn">
                <Bookmark size={14} className="mr-1" /> {markedForReview[currentQ?.id] ? 'Unmark' : 'Mark for Review'}
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={currentIndex === 0}
                onClick={() => goToQuestion(currentIndex - 1)} data-testid="prev-btn">
                <ChevronLeft size={14} /> Prev
              </Button>
              {currentIndex < questions.length - 1 ? (
                <Button size="sm" className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white"
                  onClick={() => goToQuestion(currentIndex + 1)} data-testid="save-next-btn">
                  Save & Next <ChevronRight size={14} />
                </Button>
              ) : (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" disabled={submitting} data-testid="submit-test-btn">
                      <Send size={14} className="mr-1" /> Submit Test
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-['Outfit']">Submit Test?</AlertDialogTitle>
                      <AlertDialogDescription>
                        <div className="space-y-2 mt-2">
                          <p>Answered: <strong className="text-green-600">{answeredCount}</strong></p>
                          <p>Not Answered: <strong className="text-red-500">{Math.max(0, questions.length - answeredCount)}</strong></p>
                          <p>Marked for Review: <strong className="text-[#7551e8]">{markedCount}</strong></p>
                          <p className="text-sm mt-3">Are you sure you want to submit?</p>
                        </div>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleSubmit} className="bg-green-600 hover:bg-green-700 text-white" data-testid="confirm-submit-btn">
                        Yes, Submit
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        </div>

        {/* Question Palette - Desktop */}
        <div className={`w-64 border-l border-zinc-200 bg-white p-4 overflow-y-auto shrink-0 ${showPalette ? 'fixed inset-0 z-50 w-full lg:relative lg:w-64' : 'hidden lg:block'}`} data-testid="question-palette">
          {showPalette && (
            <div className="lg:hidden flex justify-between items-center mb-4">
              <h3 className="font-['Outfit'] font-medium">Question Palette</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowPalette(false)}>Close</Button>
            </div>
          )}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-1.5 text-xs">
              <div className="flex items-center gap-1.5"><div className="w-4 h-4 rounded bg-[#10B981]" /> Answered ({answeredCount})</div>
              <div className="flex items-center gap-1.5"><div className="w-4 h-4 rounded bg-[#EF4444]" /> Not Answered</div>
              <div className="flex items-center gap-1.5"><div className="w-4 h-4 rounded bg-zinc-200" /> Not Visited</div>
              <div className="flex items-center gap-1.5"><div className="w-4 h-4 rounded bg-[#7551e8]" /> Marked ({markedCount})</div>
            </div>
            <div className="border-t border-zinc-200 pt-3">
              <div className="grid grid-cols-5 gap-1.5">
                {questions.map((q, i) => {
                  const status = getQuestionStatus(q.id);
                  const cls = status === 'answered' ? 'q-answered' : status === 'marked' ? 'q-marked' : status === 'not-answered' ? 'q-not-answered' : 'q-not-visited';
                  return (
                    <button key={q.id} onClick={() => goToQuestion(i)}
                      className={`question-palette-btn ${cls} ${currentIndex === i ? 'ring-2 ring-[#0A0A0A]' : ''}`}
                      data-testid={`palette-q-${i + 1}`}>
                      {i + 1}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
